(() => {
  if (window.__augCommerceTrackingInitialized) return;
  window.__augCommerceTrackingInitialized = true;

  const handledEvents = new WeakSet();
  const GA4_MEASUREMENT_ID = 'G-53DP5LFMP0';
  let pendingCtaSource = null;
  let pendingCtaSourceAt = 0;
  let pendingAddAttempt = null;
  let lastCheckoutInteraction = null;
  const viewedCtaSources = new Set();

  const ensureGoogleTag = () => {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };

    if (!window.__augCommerceGa4Configured) {
      window.__augCommerceGa4Configured = true;
      window.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });
    }
  };

  const pushEvent = (eventName, payload = {}) => {
    ensureGoogleTag();
    window.gtag('event', eventName, {
      ...payload,
      send_to: GA4_MEASUREMENT_ID,
      transport_type: 'beacon',
    });
  };

  const getElement = (target) => (target instanceof Element ? target : target?.parentElement);

  const createAttemptId = () =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  const getContext = (element) => element?.closest('[data-product-handle]');

  const getSelectedVariant = (element) => {
    const productInfo = element?.closest('product-info');
    const variantJson = productInfo?.querySelector('variant-selects [data-selected-variant]');
    if (!variantJson) return null;

    try {
      return JSON.parse(variantJson.textContent);
    } catch (_error) {
      return null;
    }
  };

  const getProductPayload = (element) => {
    const context = getContext(element);
    const selectedVariant = getSelectedVariant(element);

    return {
      product_handle: context?.dataset.productHandle || '',
      product_sku: selectedVariant?.sku || context?.dataset.productSku || '',
      product_title: context?.dataset.productTitle || '',
    };
  };

  const getCtaSource = () => {
    if (pendingCtaSource && Date.now() - pendingCtaSourceAt < 2000) return pendingCtaSource;
    return 'main';
  };

  const getTrackedProductForm = () =>
    document.querySelector('product-form[data-aug-track-context="official-store"]');

  const trackCtaView = (source, element) => {
    if (viewedCtaSources.has(source)) return;
    viewedCtaSources.add(source);

    pushEvent('official_store_cta_view', {
      ...getProductPayload(element || getTrackedProductForm()),
      cta_source: source,
    });
  };

  const getCheckoutSource = (element) => {
    if (element?.closest('cart-drawer, #CartDrawer')) return 'cart_drawer';
    if (element?.closest('cart-notification, #cart-notification, .cart-notification-wrapper')) {
      return 'cart_notification';
    }
    if (element?.closest('#main-cart-footer, .cart__footer, main-cart-footer')) return 'cart_page';
    return 'other_cart';
  };

  const getCartGuard = (element, source = getCheckoutSource(element)) => {
    const localRoot = element?.closest(
      'cart-drawer, #CartDrawer, cart-notification, #cart-notification, .cart-notification-wrapper, cart-items'
    );
    const localGuard = localRoot?.querySelector('[data-aug-cart-guard]');
    if (localGuard) return localGuard;

    if (source === 'cart_drawer') {
      return document.querySelector('cart-drawer [data-aug-cart-guard], #CartDrawer [data-aug-cart-guard]');
    }
    if (source === 'cart_notification') {
      return document.querySelector(
        'cart-notification [data-aug-cart-guard], #cart-notification [data-aug-cart-guard]'
      );
    }
    if (source === 'cart_page') {
      return document.querySelector('cart-items [data-aug-cart-guard], #main-cart-items [data-aug-cart-guard]');
    }

    return Array.from(document.querySelectorAll('[data-aug-cart-guard]')).sort(
      (a, b) => Number(b.dataset.augCartItemCount || 0) - Number(a.dataset.augCartItemCount || 0)
    )[0];
  };

  const getCartPayload = (element, source) => {
    const guard = getCartGuard(element, source);
    if (!guard) return { cart_state_available: 0 };

    const totalCents = Number(guard.dataset.augCartTotal || 0);
    const originalTotalCents = Number(guard.dataset.augCartOriginalTotal || totalCents);
    const discountCents = Number(guard.dataset.augCartDiscountTotal || 0);
    const thresholdCents = Number(guard.dataset.augFreeShippingThreshold || 0);
    const remainingCents = Math.max(0, thresholdCents - totalCents);

    return {
      cart_state_available: 1,
      currency: guard.dataset.augCartCurrency || 'JPY',
      cart_total_value: totalCents / 100,
      cart_original_total_value: originalTotalCents / 100,
      cart_discount_value: discountCents / 100,
      cart_has_discount: discountCents > 0 ? 1 : 0,
      cart_item_count: Number(guard.dataset.augCartItemCount || 0),
      free_shipping_threshold_value: thresholdCents / 100,
      free_shipping_remaining_value: remainingCents / 100,
      free_shipping_state:
        thresholdCents <= 0 ? 'not_configured' : totalCents >= thresholdCents ? 'qualified' : 'remaining',
      restricted_line_count: Number(guard.dataset.augRestrictedLineCount || 0),
    };
  };

  const trackCheckout = (element, interactionMethod) => {
    const now = Date.now();
    const source = getCheckoutSource(element);

    if (
      lastCheckoutInteraction &&
      lastCheckoutInteraction.source === source &&
      now - lastCheckoutInteraction.at < 1200
    ) {
      return;
    }

    lastCheckoutInteraction = { source, at: now };
    pushEvent('official_store_checkout_click', {
      ...getCartPayload(element, source),
      attempt_id: element?.dataset?.augCheckoutAttemptId || createAttemptId(),
      checkout_source: source,
      interaction_method: interactionMethod,
      destination_url: element?.href || `${window.location.origin}/checkout`,
    });
  };

  const finishAddAttempt = (eventName, event = {}) => {
    if (!pendingAddAttempt) return;
    if (event.source && event.source !== 'product-form') return;

    const attempt = pendingAddAttempt;
    const eventVariantId = String(event.productVariantId || '');
    if (eventVariantId && attempt.variantId && eventVariantId !== attempt.variantId) return;
    pendingAddAttempt = null;

    pushEvent(eventName, {
      ...attempt.product,
      attempt_id: attempt.id,
      cta_source: attempt.ctaSource,
      variant_id: String(event.productVariantId || attempt.variantId || ''),
      quantity: attempt.quantity,
      elapsed_ms: Math.max(0, Date.now() - attempt.startedAt),
      ...(eventName === 'official_store_add_to_cart_error'
        ? { error_code: String(event.code || event.status || 'UNKNOWN') }
        : {}),
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = getElement(event.target);
      if (!target) return;

      if (target.closest('[data-aug-sticky-btn]')) {
        pendingCtaSource = 'sticky';
        pendingCtaSourceAt = Date.now();
        return;
      }

      if (target.closest('product-form[data-aug-track-context="official-store"] .product-form__submit')) {
        const isStickyForwardedClick =
          pendingCtaSource === 'sticky' &&
          Date.now() - pendingCtaSourceAt < 2000 &&
          event.isTrusted === false;

        if (!isStickyForwardedClick) {
          pendingCtaSource = 'main';
          pendingCtaSourceAt = Date.now();
        }
      }
    },
    true
  );

  const observeCtaViews = () => {
    if (typeof IntersectionObserver !== 'function') return;

    const targets = [
      {
        source: 'main',
        element: document.querySelector(
          'product-form[data-aug-track-context="official-store"] .product-form__submit'
        ),
      },
      { source: 'sticky', element: document.querySelector('[data-aug-sticky-btn]') },
    ].filter((item) => item.element);

    if (!targets.length) return;

    const targetSources = new Map(targets.map((item) => [item.element, item.source]));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          const source = targetSources.get(entry.target);
          if (!source) return;
          trackCtaView(source, source === 'sticky' ? getTrackedProductForm() : entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    targets.forEach((item) => observer.observe(item.element));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCtaViews, { once: true });
  } else {
    observeCtaViews();
  }

  document.addEventListener('click', (event) => {
    if (handledEvents.has(event)) return;

    const target = getElement(event.target);
    if (!target) return;

    const amazonLink = target.closest('a[data-aug-track="amazon-buy"]');
    if (amazonLink) {
      handledEvents.add(event);
      const context = getContext(amazonLink);
      pushEvent('amazon_buy_click', {
        ...getProductPayload(amazonLink),
        amazon_asin: context?.dataset.amazonAsin || '',
        destination_url: amazonLink.href,
      });
      return;
    }

    const reviewLink = target.closest('a[data-review-site]');
    if (reviewLink) {
      handledEvents.add(event);
      pushEvent('external_review_click', {
        ...getProductPayload(reviewLink),
        review_site: reviewLink.dataset.reviewSite || '',
        destination_url: reviewLink.href,
      });
      return;
    }

    const comparisonLink = target.closest('a[data-aug-cro-link="comparison"]');
    if (comparisonLink) {
      handledEvents.add(event);
      pushEvent('product_comparison_click', {
        ...getProductPayload(comparisonLink),
        destination_url: comparisonLink.href,
      });
      return;
    }

    const checkoutControl = target.closest(
      'button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout, a[href*="/checkout"]'
    );
    if (checkoutControl) {
      handledEvents.add(event);
      trackCheckout(checkoutControl, 'click');
    }
  });

  document.addEventListener('aug:cart-guard', (event) => {
    const detail = event.detail || {};
    const source = detail.checkout_source || 'other_cart';
    const eventNames = {
      start: 'official_store_checkout_guard_start',
      result: 'official_store_checkout_guard_result',
      error: 'official_store_checkout_guard_error',
      timeout: 'official_store_checkout_guard_timeout',
    };
    const eventName = eventNames[detail.stage];
    if (!eventName) return;

    const payload = {
      ...getCartPayload(null, source),
      attempt_id: String(detail.attempt_id || ''),
      checkout_source: source,
      guard_result: String(detail.guard_result || ''),
      error_code: String(detail.error_code || ''),
      restricted_line_count: Number(detail.restricted_line_count || 0),
      removed_line_count: Number(detail.removed_line_count || 0),
      removal_failed_count: Number(detail.removal_failed_count || 0),
      elapsed_ms: Number(detail.elapsed_ms || 0),
    };

    if (detail.stage === 'start') {
      pushEvent('official_store_checkout_intent', payload);
    }
    pushEvent(eventName, payload);
  });

  document.addEventListener(
    'submit',
    (event) => {
      if (handledEvents.has(event)) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;

      const productForm = form.closest('product-form[data-aug-track-context="official-store"]');
      if (!productForm) return;

      const submitter = event.submitter;
      if (submitter?.disabled || submitter?.getAttribute?.('aria-disabled') === 'true') return;
      if (pendingAddAttempt && Date.now() - pendingAddAttempt.startedAt < 15000) return;

      handledEvents.add(event);
      const variantField = form.elements.namedItem('id');
      const quantityField = form.elements.namedItem('quantity');
      const ctaSource = getCtaSource();
      const attemptId = createAttemptId();
      const variantId = variantField && 'value' in variantField ? String(variantField.value) : '';
      const quantity = quantityField && 'value' in quantityField ? String(quantityField.value) : '1';

      pendingAddAttempt = {
        id: attemptId,
        ctaSource,
        variantId,
        quantity,
        product: getProductPayload(productForm),
        startedAt: Date.now(),
      };
      pendingCtaSource = null;
      pendingCtaSourceAt = 0;

      pushEvent('official_store_add_to_cart_click', {
        ...pendingAddAttempt.product,
        attempt_id: attemptId,
        cta_source: ctaSource,
        variant_id: variantId,
        quantity,
        destination_url: form.action,
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart"]')) return;
      if (form.matches('form[action*="/cart/add"]')) return;

      const submitter = event.submitter;
      const checkoutSelector = 'button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout';
      if (!submitter?.matches?.(checkoutSelector)) return;

      trackCheckout(submitter, 'submit');
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      finishAddAttempt('official_store_add_to_cart_success', event || {});
    });

    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      finishAddAttempt('official_store_add_to_cart_error', {
        ...(event || {}),
        code: event?.code || event?.status || 'INVALID',
      });
    });
  }

  document.addEventListener(
    'shopify:cart:error',
    (event) => {
      const target = getElement(event.target);
      if (!target?.closest('product-form[data-aug-track-context="official-store"]')) return;

      finishAddAttempt('official_store_add_to_cart_error', {
        source: 'product-form',
        code: event?.code || 'SERVICE_UNAVAILABLE',
      });
    },
    true
  );

  document.addEventListener(
    'toggle',
    (event) => {
      if (handledEvents.has(event)) return;

      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-aug-cro-detail="fit"]')) return;
      if (!details.open) return;

      handledEvents.add(event);
      pushEvent('purchase_fit_open', getProductPayload(details));
    },
    true
  );
})();
